import { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "motion/react";
import { Link } from "react-router-dom";
import logo from "@/assets/Logo.png";
import { ChevronDown } from "lucide-react";

/* ─── TYPES ──────────────────────────────────── */
interface FooterProps {
  setIsContactModalOpen: (open: boolean) => void;
}

/* ─── DATA ───────────────────────────────────── */
const servicesLinks = [
  { to: "/services/3d-animation", label: "3D & Animation" },
  { to: "/services/ar-vr", label: "Technology" },
  { to: "/services/hardware", label: "Hardware" },
];

const companyLinks = [
  { to: "/about", label: "About" },
  { to: "/work", label: "Work" },
  { to: "/blog", label: "Blog" },
  { href: "#", label: "Careers" },
];

const industryClusters = [
  {
    category: "Commerce & Enterprise",
    dot: "bg-blue-400",
    items: [
      "Real Estate",
      "Banking & Finance",
      "Retail & E-commerce",
      "Corporate & Enterprise",
    ],
  },
  {
    category: "Media & Experiences",
    dot: "bg-purple-400",
    items: [
      "Advertising & Media",
      "Entertainment & Gaming",
      "Tourism & Hospitality",
    ],
  },
  {
    category: "Tech & Institutional",
    dot: "bg-emerald-400",
    items: [
      "Healthcare & Medical",
      "Education & Training",
      "Manufacturing & Industrial",
    ],
  },
];

const industryLinks = industryClusters.flatMap((c) => c.items);

const connectLinks = [
  {
    href: "tel:+919993497855",
    label: "+91 99934 97855",
    isPhone: true,
    phone: "+91 99934 97855",
  },
  {
    href: "https://www.linkedin.com/company/kivas-tech/posts/?feedView=all",
    label: "LinkedIn",
  },
  {
    href: "https://www.instagram.com/kivas_tech?igsh=Z3hkN2kxbXhjajRh",
    label: "Instagram",
  },
  { href: "mailto:info@kivastech.com", label: "info@kivastech.com" },
];

/* ─── HELPERS ────────────────────────────────── */
const EASE = [0.16, 1, 0.3, 1] as const;
const MONTSERRAT: React.CSSProperties = {
  fontFamily: "'Montserrat', sans-serif",
};

/* ─── COLUMN HEADING ─────────────────────────── */
function ColHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3
      className="text-[0.6rem] font-semibold tracking-[0.22em] uppercase text-white/35 mb-6"
      style={MONTSERRAT}
    >
      {children}
    </h3>
  );
}

/* ─── NAV LINK ───────────────────────────────── */
function NavLink({
  href,
  to,
  children,
}: {
  href?: string;
  to?: string;
  children: React.ReactNode;
}) {
  const cls =
    "group relative inline-flex items-center gap-2 text-sm text-white/45 hover:text-white transition-colors duration-300 interactive";

  const inner = (
    <>
      <span className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
      {children}
    </>
  );

  if (to)
    return (
      <Link to={to} className={cls} style={MONTSERRAT}>
        {inner}
      </Link>
    );
  if (href)
    return (
      <a href={href} className={cls} style={MONTSERRAT}>
        {inner}
      </a>
    );
  return (
    <span className={cls} style={MONTSERRAT}>
      {inner}
    </span>
  );
}

/* ─── PHONE LINK ─────────────────────────────── */
function PhoneLink({
  href,
  phone,
  children,
}: {
  href: string;
  phone: string;
  children: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (window.innerWidth >= 768) {
      e.preventDefault();
      navigator.clipboard.writeText(phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      className="group relative inline-flex items-center gap-2 text-sm text-white/45 hover:text-white transition-colors duration-300 interactive cursor-pointer"
      style={MONTSERRAT}
    >
      <span className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
      {children}
      <AnimatePresence>
        {copied && (
          <motion.span
            initial={{ opacity: 0, scale: 0.8, x: -4 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="text-[0.6rem] font-semibold uppercase tracking-wider text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-1.5 py-0.5 rounded"
          >
            Copied
          </motion.span>
        )}
      </AnimatePresence>
    </a>
  );
}

/* ─── CTA BUTTON ─────────────────────────────── */
function CTAButton({ onClick }: { onClick: () => void }) {
  const ref = useRef<HTMLButtonElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const r = ref.current!.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2);
    const dy = e.clientY - (r.top + r.height / 2);
    ref.current!.style.transform = `translate(${dx * 0.22}px, ${dy * 0.22}px)`;
  };

  const handleMouseLeave = () => {
    ref.current!.style.transform = "";
    ref.current!.style.transition =
      "transform 0.6s cubic-bezier(0.34,1.56,0.64,1)";
    setTimeout(() => {
      if (ref.current) ref.current.style.transition = "";
    }, 650);
  };

  return (
    <motion.button
      ref={ref}
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="group relative flex items-center justify-center gap-3 bg-white text-black px-8 sm:px-9 py-3.5 sm:py-4 rounded-full text-[0.68rem] sm:text-[0.7rem] lg:text-[0.65rem] font-semibold uppercase tracking-[0.16em] hover:bg-white/90 active:scale-95 transition-all duration-300 shrink-0 overflow-hidden interactive cursor-pointer shadow-xl shadow-white/5"
      style={MONTSERRAT}
    >
      {/* Shimmer sweep */}
      <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-black/5 to-transparent pointer-events-none" />
      Talk to us
      <span className="w-2 h-2 rounded-full bg-black group-hover:scale-150 transition-transform duration-300 flex-shrink-0" />
    </motion.button>
  );
}

/* ─── INDUSTRY COLUMN ────────────────────────── */
function IndustryColumn() {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? industryLinks : industryLinks.slice(0, 4);

  return (
    <div>
      <ColHeading>Industry</ColHeading>
      <ul className="flex flex-col gap-3.5">
        <AnimatePresence initial={false}>
          {visible.map((label, i) => (
            <motion.li
              key={label}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.3, delay: i * 0.04, ease: EASE }}
            >
              <NavLink href="#">{label}</NavLink>
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="flex items-center gap-1.5 mt-5 text-[0.62rem] font-semibold tracking-[0.14em] uppercase text-white/30 hover:text-white/60 transition-colors duration-200 interactive"
        style={MONTSERRAT}
      >
        {expanded ? "Less" : "More"}
        <motion.svg
          width="10"
          height="10"
          viewBox="0 0 12 12"
          fill="none"
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </motion.svg>
      </button>
    </div>
  );
}

/* ─── FOOTER ─────────────────────────────────── */
export default function Footer({ setIsContactModalOpen }: FooterProps) {
  const heroRef = useRef<HTMLDivElement>(null);
  const heroView = useInView(heroRef, { once: true, amount: 0.4 });
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [mobileIndustryExpanded, setMobileIndustryExpanded] = useState(false);

  const toggleSection = (section: string) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  return (
    <footer className="relative bg-[#050505] text-white overflow-hidden z-10">
      {/* ── Ambient glows ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-indigo-600/[0.04] blur-[120px]" />
        <div className="absolute -bottom-20 right-0 w-[400px] h-[400px] rounded-full bg-purple-600/[0.04] blur-[100px]" />
      </div>

      {/* ── Noise texture ── */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E")`,
          backgroundSize: "200px 200px",
        }}
      />

      <div className="relative z-10">
        {/* ══ HERO STATEMENT ══ */}
        <div
          ref={heroRef}
          className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-16 sm:pt-20 lg:pt-24 pb-0"
        >
          <div className="flex flex-col items-center text-center lg:flex-row lg:justify-between lg:items-end lg:text-left gap-6 sm:gap-8 lg:gap-10 mb-12 lg:mb-16">
            {/* Giant wordmark */}
            <div className="overflow-hidden">
              <motion.h2
                initial={{ y: "105%", opacity: 0 }}
                animate={heroView ? { y: "0%", opacity: 1 } : {}}
                transition={{ duration: 1.1, ease: EASE }}
                style={MONTSERRAT}
                className="text-[clamp(4.2rem,17vw,14rem)] font-bold tracking-tight leading-[0.85] text-white text-center lg:text-left"
              >
                KIVAS
              </motion.h2>
            </div>

            <div className="flex flex-col items-center lg:items-end gap-5 sm:gap-6 pb-2 text-center lg:text-right w-full lg:w-auto">
              {/* Tagline */}
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={heroView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, ease: EASE, delay: 0.35 }}
                className="text-xs sm:text-sm font-light text-white/50 lg:text-white/35 max-w-[320px] sm:max-w-[400px] lg:max-w-[220px] leading-relaxed mx-auto lg:mx-0"
                style={MONTSERRAT}
              >
                Engineering immersive experiences across 3D, AR/VR, and advanced
                hardware.
              </motion.p>
              <CTAButton onClick={() => setIsContactModalOpen(true)} />
            </div>
          </div>

          {/* Top divider — animated draw */}
          <motion.div
            initial={{ scaleX: 0, originX: 0 }}
            animate={heroView ? { scaleX: 1 } : {}}
            transition={{ duration: 1, ease: EASE, delay: 0.5 }}
            className="h-px bg-gradient-to-r from-white/10 via-indigo-500/20 to-transparent mb-0"
          />
        </div>

        {/* ══ LINKS SECTION ══ */}
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-12 md:pt-16">
          {/* ── MOBILE ACCORDION VIEW (< md) ── */}
          <div className="md:hidden flex flex-col pb-12 border-b border-white/[0.08]">
            {/* Centered Logo & Brand Bio */}
            <div className="flex flex-col items-center text-center gap-3 pb-8 border-b border-white/[0.08]">
              <div className="relative">
                <div className="absolute -inset-2 bg-indigo-500/10 rounded-full blur-lg" />
                <img
                  src={logo}
                  alt="KIVAS"
                  className="relative w-12 h-12 object-contain"
                />
              </div>

              {/* Two-Tier Statement */}
              <div className="flex flex-col gap-1 mt-1">
                <h3
                  className="text-xs font-semibold text-white/90 tracking-wide"
                  style={MONTSERRAT}
                >
                  Where creativity meets intelligence.
                </h3>
                <p
                  className="text-[0.7rem] font-light text-white/40 max-w-[280px] leading-relaxed mx-auto"
                  style={MONTSERRAT}
                >
                  Redefining what's possible at the edge of digital and physical
                  reality.
                </p>
              </div>
            </div>

            {/* Accordion 1: Services */}
            <div className="border-b border-white/[0.08]">
              <button
                type="button"
                onClick={() => toggleSection("services")}
                className="w-full py-4 flex items-center justify-between text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors"
                style={MONTSERRAT}
              >
                <span>Services</span>
                <ChevronDown
                  className={`w-4 h-4 text-white/40 transition-transform duration-300 ${
                    openSection === "services" ? "rotate-180 text-white" : ""
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {openSection === "services" && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="overflow-hidden pb-4"
                  >
                    <ul className="flex flex-col gap-3 pl-2">
                      {servicesLinks.map((l) => (
                        <li key={l.label}>
                          <NavLink to={l.to}>{l.label}</NavLink>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Accordion 2: Company */}
            <div className="border-b border-white/[0.08]">
              <button
                type="button"
                onClick={() => toggleSection("company")}
                className="w-full py-4 flex items-center justify-between text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors"
                style={MONTSERRAT}
              >
                <span>Company</span>
                <ChevronDown
                  className={`w-4 h-4 text-white/40 transition-transform duration-300 ${
                    openSection === "company" ? "rotate-180 text-white" : ""
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {openSection === "company" && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="overflow-hidden pb-4"
                  >
                    <ul className="flex flex-col gap-3 pl-2">
                      {companyLinks.map((l) => (
                        <li key={l.label}>
                          <NavLink to={l.to} href={l.href}>
                            {l.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Accordion 3: Industry */}
            <div className="border-b border-white/[0.08]">
              <button
                type="button"
                onClick={() => toggleSection("industry")}
                className="w-full py-4 flex items-center justify-between text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors"
                style={MONTSERRAT}
              >
                <span>Industries</span>
                <ChevronDown
                  className={`w-4 h-4 text-white/40 transition-transform duration-300 ${
                    openSection === "industry" ? "rotate-180 text-white" : ""
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {openSection === "industry" && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="overflow-hidden pb-4"
                  >
                    <ul className="grid grid-cols-2 gap-x-4 gap-y-3 pl-2">
                      <AnimatePresence initial={false}>
                        {(mobileIndustryExpanded
                          ? industryLinks
                          : industryLinks.slice(0, 4)
                        ).map((label, i) => (
                          <motion.li
                            key={label}
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{
                              duration: 0.25,
                              delay: i * 0.02,
                              ease: EASE,
                            }}
                          >
                            <NavLink href="#">{label}</NavLink>
                          </motion.li>
                        ))}
                      </AnimatePresence>
                    </ul>
                    <div className="w-full flex justify-center pt-3">
                      <button
                        type="button"
                        onClick={() => setMobileIndustryExpanded((p) => !p)}
                        className="flex items-center justify-center gap-1.5 text-[0.62rem] font-semibold tracking-[0.14em] uppercase text-indigo-400 hover:text-indigo-300 transition-colors cursor-pointer"
                        style={MONTSERRAT}
                      >
                        <span>
                          {mobileIndustryExpanded
                            ? "− Show Less"
                            : `+ ${industryLinks.length - 4} More Industries`}
                        </span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Accordion 4: Connect */}
            <div className="border-b border-white/[0.08]">
              <button
                type="button"
                onClick={() => toggleSection("connect")}
                className="w-full py-4 flex items-center justify-between text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors"
                style={MONTSERRAT}
              >
                <span>Connect</span>
                <ChevronDown
                  className={`w-4 h-4 text-white/40 transition-transform duration-300 ${
                    openSection === "connect" ? "rotate-180 text-white" : ""
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {openSection === "connect" && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="overflow-hidden pb-4"
                  >
                    <ul className="flex flex-col gap-3 pl-2">
                      {connectLinks.map((l) => (
                        <li key={l.label}>
                          {l.isPhone ? (
                            <PhoneLink href={l.href} phone={l.phone}>
                              {l.label}
                            </PhoneLink>
                          ) : (
                            <NavLink href={l.href}>{l.label}</NavLink>
                          )}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* ── DESKTOP / TABLET GRID VIEW (>= md) ── */}
          <div className="hidden md:grid grid-cols-2 md:grid-cols-12 gap-x-8 gap-y-12 pb-20 border-b border-white/[0.06]">
            {/* Logo + tagline */}
            <div className="col-span-2 md:col-span-4 flex flex-col gap-4">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.65, ease: EASE }}
                className="flex items-center gap-3"
              >
                <img
                  src={logo}
                  alt="KIVAS"
                  className="w-12 h-12 object-contain"
                />
              </motion.div>
              <div className="flex flex-col gap-1.5 max-w-[340px]">
                <motion.h4
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.65, ease: EASE, delay: 0.1 }}
                  className="text-sm font-semibold text-white/90 tracking-wide whitespace-nowrap"
                  style={MONTSERRAT}
                >
                  Where creativity meets intelligence.
                </motion.h4>
                <motion.p
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.65, ease: EASE, delay: 0.15 }}
                  className="text-xs font-light text-white/35 leading-relaxed max-w-[280px]"
                  style={MONTSERRAT}
                >
                  Redefining what's possible at the edge of digital and physical
                  reality.
                </motion.p>
              </div>
            </div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
              className="col-span-1 md:col-span-2"
            >
              <ColHeading>Services</ColHeading>
              <ul className="flex flex-col gap-3.5">
                {servicesLinks.map((l) => (
                  <li key={l.label}>
                    <NavLink to={l.to}>{l.label}</NavLink>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Company */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.15 }}
              className="col-span-1 md:col-span-2"
            >
              <ColHeading>Company</ColHeading>
              <ul className="flex flex-col gap-3.5">
                {companyLinks.map((l) => (
                  <li key={l.label}>
                    <NavLink to={l.to} href={l.href}>
                      {l.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Industry */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.2 }}
              className="col-span-1 md:col-span-2"
            >
              <IndustryColumn />
            </motion.div>

            {/* Connect */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.25 }}
              className="col-span-1 md:col-span-2"
            >
              <ColHeading>Connect</ColHeading>
              <ul className="flex flex-col gap-3.5">
                {connectLinks.map((l) => (
                  <li key={l.label}>
                    {l.isPhone ? (
                      <PhoneLink href={l.href} phone={l.phone}>
                        {l.label}
                      </PhoneLink>
                    ) : (
                      <NavLink href={l.href}>{l.label}</NavLink>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* ══ BOTTOM BAR ══ */}
          <div className="pt-7 pb-10 flex flex-col md:flex-row justify-between items-center gap-4">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
              className="text-[0.58rem] font-semibold tracking-[0.18em] uppercase text-white/20"
              style={MONTSERRAT}
            >
              © 2026 Kivas. All rights reserved.
            </motion.span>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.2 }}
              className="flex gap-6"
            >
              {[
                { to: "/privacy", label: "Privacy" },
                { to: "/terms", label: "Terms" },
              ].map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  className="text-[0.58rem] font-semibold tracking-[0.18em] uppercase text-white/20 hover:text-white/50 transition-colors duration-200 interactive"
                  style={MONTSERRAT}
                >
                  {l.label}
                </Link>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
}
